import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { StaggerHeading } from '@/components/StaggerHeading';

/**
 * Threads — Figure 3.
 *
 * A two-lane race: single-threaded compression on top, four-thread
 * compression below. Autoplays when scrolled into view; replay button
 * underneath.
 *
 * Timings come from a fixture that compresses cleanly: four files of
 * roughly the same size compress in ~200ms each. Sequential = 842 ms,
 * parallel-on-4 = 220 ms (the slowest single file dominates).
 *
 * Below the race, an honest paragraph: threading helps when there are
 * many files of similar size. With one large file or many tiny files,
 * the speedup goes away.
 */

type RaceFile = {
  name: string;
  ms: number;
  kib: number;
  /** Coral-family shade. Same shade is used for the same file in both
   *  lanes so the eye can match a thread back to its file. */
  shade: string;
};

const FILES: RaceFile[] = [
  { name: 'main.cpp', ms: 200, kib: 42, shade: 'var(--color-coral-400)' },
  { name: 'lzss.cpp', ms: 220, kib: 38, shade: 'var(--color-coral-500)' },
  { name: 'archive.cpp', ms: 215, kib: 51, shade: 'var(--color-coral-600)' },
  { name: 'crc32.hpp', ms: 207, kib: 28, shade: 'var(--color-coral-700)' },
];

const SINGLE_TOTAL_MS = FILES.reduce((s, f) => s + f.ms, 0); // 842
const MULTI_TOTAL_MS = Math.max(...FILES.map((f) => f.ms)); // 220
const SPEEDUP = SINGLE_TOTAL_MS / MULTI_TOTAL_MS; // ~3.83

// Visual duration of the longer (single-threaded) lane, in ms.
const VISUAL_SINGLE_MS = 2400;
const TIME_SCALE = VISUAL_SINGLE_MS / SINGLE_TOTAL_MS; // ~2.85
const VISUAL_MULTI_MS = MULTI_TOTAL_MS * TIME_SCALE; // ~628

export function Threads() {
  const sectionRef = useRef<HTMLElement>(null);
  const reduced = useReducedMotion() ?? false;
  const [runId, setRunId] = useState(0);
  const [phase, setPhase] = useState<'idle' | 'running' | 'done'>('idle');

  // Autoplay on first scroll into view.
  useEffect(() => {
    if (reduced) {
      setPhase('done');
      return;
    }
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setRunId((n) => n + 1);
            setPhase('running');
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [reduced]);

  // After the visual race ends, mark phase done so the replay UI shows.
  useEffect(() => {
    if (phase !== 'running') return;
    const t = setTimeout(() => setPhase('done'), VISUAL_SINGLE_MS + 200);
    return () => clearTimeout(t);
  }, [phase, runId]);

  const replay = () => {
    setRunId((n) => n + 1);
    setPhase('running');
  };

  // Animate the speedup readout from 1.0× to the real value over the
  // race's visual duration. Keyed on runId so replay restarts it.
  const speedup = useSpeedupTicker(runId, phase, reduced);

  return (
    <section
      ref={sectionRef}
      id="threads"
      style={{ padding: '160px 0', position: 'relative' }}
    >
      <div className="container-x">
        <span
          className="t-mono"
          style={{
            fontSize: 'var(--type-xs)',
            color: 'var(--accent)',
            letterSpacing: 'var(--tracking-mono-caps)',
            textTransform: 'uppercase',
          }}
        >
          figure 3
        </span>
        <StaggerHeading
          as="h2"
          className="t-heading"
          stagger={0.04}
          style={{
            marginTop: 16,
            fontSize: 'var(--type-xl)',
            maxWidth: '20ch',
          }}
        >
          When threading helps.
        </StaggerHeading>
        <p
          style={{
            marginTop: 20,
            fontSize: 'var(--type-md)',
            color: 'var(--text-2)',
            maxWidth: '60ch',
            lineHeight: 1.55,
          }}
        >
          Four files, compressed two ways. The same engine runs the race —
          only the thread count differs.
        </p>

        {/* The race */}
        <div
          style={{
            marginTop: 64,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 16,
            padding: 32,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* No decorative axis. The bars + ms counters carry the rhythm.
           *  A non-proportional tick layer would be visual lying. */}
          <Lane
            kind="single"
            runId={runId}
            badge="1 thread"
            totalMs={SINGLE_TOTAL_MS}
            visualMs={VISUAL_SINGLE_MS}
            files={FILES}
          />
          <div style={{ height: 28 }} />
          <Lane
            kind="multi"
            runId={runId}
            badge="4 threads"
            totalMs={MULTI_TOTAL_MS}
            visualMs={VISUAL_MULTI_MS}
            files={FILES}
          />

          {/* Speedup readout */}
          <div
            style={{
              marginTop: 36,
              paddingTop: 24,
              borderTop: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--type-xl)',
                  fontWeight: 600,
                  letterSpacing: 'var(--tracking-tight)',
                  color: 'var(--accent)',
                }}
              >
                {speedup.toFixed(1)}×
              </span>
              <span style={{ fontSize: 'var(--type-md)', color: 'var(--text-2)' }}>
                faster on four threads
              </span>
            </div>
            <button
              onClick={replay}
              className="t-mono replay-btn"
              style={{
                fontSize: 'var(--type-xs)',
                letterSpacing: 'var(--tracking-mono-caps)',
                textTransform: 'uppercase',
                padding: '12px 20px',
                minHeight: 44,
                border: '1px solid var(--border-strong)',
                borderRadius: 'var(--radius-pill)',
                background: 'transparent',
                color: 'var(--text-2)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                transition:
                  'background var(--dur-fast) var(--ease-out-expo), color var(--dur-fast) var(--ease-out-expo), border-color var(--dur-fast) var(--ease-out-expo)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--text)';
                e.currentTarget.style.borderColor = 'var(--text-3)';
                e.currentTarget.style.background = 'rgba(0,0,0,0.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--text-2)';
                e.currentTarget.style.borderColor = 'var(--border-strong)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg
                aria-hidden
                width="11"
                height="11"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="replay-icon"
              >
                <path d="M2.5 8a5.5 5.5 0 1 0 1.5-3.8" />
                <polyline points="2,2 2,5 5,5" />
              </svg>
              run again
              <style>{`
                .replay-btn:hover .replay-icon { transform: rotate(-360deg); transition: transform 800ms cubic-bezier(0.22,1,0.36,1); }
                .replay-icon { transition: transform 0ms; }
              `}</style>
            </button>
          </div>
        </div>

        {/* Honesty footer */}
        <div
          style={{
            marginTop: 32,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 20,
            paddingInline: 4,
          }}
        >
          <Note
            label="works for"
            body="Many files of similar size. Each one becomes a worker job; the wall-clock time drops to the slowest single file."
          />
          <Note
            label="no help on"
            body="One large file. LZSS compresses one byte stream serially — the thread pool has nothing to parallelise."
          />
          <Note
            label="overhead on"
            body="Hundreds of tiny files. Job dispatch costs more than compressing them. Falls back to serial below ≤ 1 thread automatically."
          />
        </div>
      </div>
    </section>
  );
}

/* ── Lane ──────────────────────────────────────────────────────────── */

function Lane({
  kind,
  runId,
  badge,
  totalMs,
  visualMs,
  files,
}: {
  kind: 'single' | 'multi';
  runId: number;
  badge: string;
  totalMs: number;
  visualMs: number;
  files: RaceFile[];
}) {
  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <span
            style={{
              fontSize: 'var(--type-md)',
              fontWeight: 600,
              color: 'var(--text)',
              letterSpacing: 'var(--tracking-tight)',
            }}
          >
            {kind === 'single' ? 'Single thread' : 'Multi-threaded'}
          </span>
          <span
            className="t-mono"
            style={{
              fontSize: 'var(--type-xs)',
              padding: '3px 10px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid',
              color: kind === 'multi' ? 'var(--accent)' : 'var(--text-3)',
              borderColor:
                kind === 'multi' ? 'var(--accent-bg-2, var(--accent-bg))' : 'var(--border-strong)',
              background: kind === 'multi' ? 'var(--accent-bg)' : 'transparent',
              letterSpacing: 'var(--tracking-mono-caps)',
              textTransform: 'uppercase',
            }}
          >
            {badge}
          </span>
        </div>
        <MsCounter
          key={`${kind}-${runId}`}
          runId={runId}
          target={totalMs}
          durationMs={visualMs}
          highlight={kind === 'multi'}
        />
      </div>

      {/* Lane body */}
      {kind === 'single' ? (
        <SingleLane runId={runId} files={files} />
      ) : (
        <MultiLane runId={runId} files={files} />
      )}
    </div>
  );
}

/* Single thread: one row, files fill sequentially. Each bar starts when
 * the previous one ends. */
function SingleLane({ runId, files }: { runId: number; files: RaceFile[] }) {
  let cumulative = 0;
  return (
    <div
      style={{
        position: 'relative',
        height: 28,
        background: 'var(--bg-warm)',
        borderRadius: 6,
        overflow: 'hidden',
        border: '1px solid var(--border)',
      }}
    >
      {files.map((f, i) => {
        const startMs = cumulative;
        cumulative += f.ms;
        const widthPct = (f.ms / SINGLE_TOTAL_MS) * 100;
        const offsetPct = (startMs / SINGLE_TOTAL_MS) * 100;
        return (
          <FileBar
            key={`${runId}-${i}`}
            file={f}
            offsetPct={offsetPct}
            widthPct={widthPct}
            delayMs={startMs * TIME_SCALE}
            durationMs={f.ms * TIME_SCALE}
          />
        );
      })}
    </div>
  );
}

/* Multi thread: four rows, each one is one worker. Each bar starts at 0
 * and lasts its file's duration. */
function MultiLane({ runId, files }: { runId: number; files: RaceFile[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {files.map((f, i) => {
        const widthPct = (f.ms / SINGLE_TOTAL_MS) * 100; // share same x-axis as single
        return (
          <div
            key={`${runId}-${i}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                width: 156,
                flexShrink: 0,
                justifyContent: 'flex-end',
              }}
            >
              <span
                className="t-mono"
                style={{
                  fontSize: 10,
                  color: 'var(--text-3)',
                  letterSpacing: 'var(--tracking-mono-caps)',
                  textTransform: 'uppercase',
                }}
              >
                thread {i}
              </span>
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: f.shade,
                  flexShrink: 0,
                }}
              />
              <span
                className="t-mono"
                style={{
                  fontSize: 10,
                  color: 'var(--text-2)',
                  letterSpacing: 0.2,
                }}
              >
                {f.name}
              </span>
            </span>
            <div
              style={{
                position: 'relative',
                flex: 1,
                height: 14,
                background: 'var(--bg-warm)',
                borderRadius: 4,
                overflow: 'hidden',
                border: '1px solid var(--border)',
              }}
            >
              <FileBar
                file={f}
                offsetPct={0}
                widthPct={widthPct}
                delayMs={0}
                durationMs={f.ms * TIME_SCALE}
                compact
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FileBar({
  file,
  offsetPct,
  widthPct,
  delayMs,
  durationMs,
  compact = false,
}: {
  file: RaceFile;
  offsetPct: number;
  widthPct: number;
  delayMs: number;
  durationMs: number;
  compact?: boolean;
}) {
  return (
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${widthPct}%` }}
      transition={{
        duration: durationMs / 1000,
        delay: delayMs / 1000,
        ease: 'linear',
      }}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: `${offsetPct}%`,
        background: file.shade,
        borderRight: '1px solid color-mix(in oklch, var(--bg-card) 60%, transparent)',
        display: 'flex',
        alignItems: 'center',
        paddingInline: compact ? 0 : 8,
        overflow: 'hidden',
      }}
    >
      {!compact && (
        <span
          className="t-mono"
          style={{
            fontSize: 10,
            color: 'var(--bg)',
            letterSpacing: 0.4,
            whiteSpace: 'nowrap',
          }}
        >
          {file.name}
        </span>
      )}
    </motion.div>
  );
}

/* ── Counters ──────────────────────────────────────────────────────── */

function MsCounter({
  runId,
  target,
  durationMs,
  highlight,
}: {
  runId: number;
  target: number;
  durationMs: number;
  highlight: boolean;
}) {
  const [v, setV] = useState(0);

  useEffect(() => {
    let raf = 0;
    setV(0);
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / durationMs);
      setV(Math.round(target * p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [runId, target, durationMs]);

  return (
    <span
      className="t-mono"
      style={{
        fontSize: 'var(--type-md)',
        fontWeight: 600,
        color: highlight ? 'var(--accent)' : 'var(--text-2)',
        letterSpacing: 'var(--tracking-tight)',
        fontVariantNumeric: 'tabular-nums',
        minWidth: 80,
        textAlign: 'right',
      }}
    >
      {v}ms
    </span>
  );
}

/* ── Notes ─────────────────────────────────────────────────────────── */

function Note({ label, body }: { label: string; body: string }) {
  return (
    <div>
      <span
        className="t-mono"
        style={{
          fontSize: 'var(--type-xs)',
          color: 'var(--text-3)',
          letterSpacing: 'var(--tracking-mono-caps)',
          textTransform: 'uppercase',
        }}
      >
        // {label}
      </span>
      <p
        style={{
          marginTop: 8,
          fontSize: 'var(--type-sm)',
          color: 'var(--text-2)',
          lineHeight: 1.55,
        }}
      >
        {body}
      </p>
    </div>
  );
}

/* ── hooks ─────────────────────────────────────────────────────────── */

/**
 * Animate the speedup readout from 1.0× to SPEEDUP across the visual
 * race duration. Restarts whenever runId changes (replay button).
 * Reduced-motion users see the final number immediately.
 */
function useSpeedupTicker(runId: number, phase: string, reduced: boolean): number {
  const [v, setV] = useState(reduced || phase === 'idle' ? SPEEDUP : 1);

  useEffect(() => {
    if (reduced) {
      setV(SPEEDUP);
      return;
    }
    if (phase !== 'running') return;
    const t0 = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / VISUAL_SINGLE_MS);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out-cubic
      setV(1 + (SPEEDUP - 1) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    setV(1);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [runId, phase, reduced]);

  return v;
}
